import { useState, useCallback, useMemo } from 'react';
import restaurantAPI from '../api/restaurantAPI';
import { getUserIdFromToken } from '../api/authAPI';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing restaurant cart/order
 * Handles: add items, remove items, update quantities, calculate totals
 */
export const useRestaurantCart = (tableNumber, transactionType = 'dine-in') => {
  const { getAccessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastError, setLastError] = useState(null);

  // Add item to cart
  const addItem = useCallback((product, quantity = 1) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.productId === product._id);

      if (existingItem) {
        return prevItems.map(item =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [
        ...prevItems,
        {
          productId: product._id,
          productName: product.name,
          productSku: product.sku,
          productCategory: product.category?.name || '',
          price: product.price,
          quantity,
          notes: '',
          image: product.image
        }
      ];
    });
    setError(null);
  }, []);

  // Remove item from cart
  const removeItem = useCallback((productId) => {
    setItems(prevItems => prevItems.filter(item => item.productId !== productId));
    setError(null);
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.productId === productId
          ? { ...item, quantity }
          : item
      )
    );
    setError(null);
  }, [removeItem]);

  // Update item notes (special requests)
  const updateItemNotes = useCallback((productId, notes) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.productId === productId
          ? { ...item, notes }
          : item
      )
    );
  }, []);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setItems([]);
    setNotes('');
    setDiscountPercent(0);
    setError(null);
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const discount = subtotal * (discountPercent / 100);
    const taxableAmount = subtotal - discount;
    const tax = transactionType !== 'takeout' ? taxableAmount * (taxPercent / 100) : 0;
    const total = taxableAmount + tax;

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      taxableAmount: parseFloat(taxableAmount.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    };
  }, [items, discountPercent, taxPercent, transactionType]);

  // Create order from cart items (NEW: uses restaurant-specific API)
  const createOrder = useCallback(async (tableId, waiterId, waiterPin) => {
    if (items.length === 0) {
      setError('Cart is empty');
      return null;
    }

    if (!tableId || !waiterId) {
      setError('Missing required parameters: tableId, waiterId');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Format products for order creation
      const products = items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price,
        note: item.notes
      }));

      // Create order via restaurant API
      const orderResponse = await restaurantAPI.orders.create(
        tableId,
        waiterId,
        waiterPin,
        products
      );

      // Note: Component should call restaurantAPI.orders.send(orderId) next for delta printing
      return orderResponse;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create order';
      setError(errorMessage);
      setLastError(errorMessage);
      console.error('Error creating order:', err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [items]);

  // Submit cart to backend (LEGACY - kept for backward compatibility)
  const submitCart = useCallback(async (paymentMethod = 'cash', tendered = 0) => {
    if (items.length === 0) {
      setError('Cart is empty');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = getAccessToken();
      const tenantId = localStorage.getItem('tenantId');
      const storeId = localStorage.getItem('storeId');

      // Validate required fields
      if (!tenantId) {
        throw new Error('Tenant information missing. Please log in again.');
      }

      if (!storeId) {
        throw new Error('Store information missing. Please select a store.');
      }

      let waiterId = user.id || user._id;
      
      // Fallback: get from token if not in localStorage
      if (!waiterId) {
        waiterId = getUserIdFromToken();
      }

      if (!waiterId) {
        throw new Error('User ID not found - please log in again');
      }

      // Use new restaurant API for order creation
      const products = items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price,
        note: item.notes
      }));

      // Note: tableNumber should be converted to tableId for new API
      // For now, fallback to old /carts endpoint if available
      const cartData = {
        tenant: tenantId,
        store: storeId,
        waiter: waiterId,
        tableNumber,
        transactionType,
        items: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
          notes: item.notes
        })),
        notes,
        discountPercent,
        taxPercent,
        ...totals,
        paymentMethod,
        tendered: parseFloat(tendered) || 0,
        changeDue: parseFloat(tendered) - totals.total,
        billedOut: false
      };

      // Try legacy endpoint first, fall back to new API
      try {
        const response = await restaurantAPI.orders.createFromCart(tableNumber, cartData);
        return response;
      } catch (err) {
        console.warn('Legacy /carts endpoint failed, attempting alternative...');
        throw new Error('Order creation failed. Please use the new order flow.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to submit cart';
      setError(errorMessage);
      setLastError(errorMessage);
      console.error('Error submitting cart:', err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [items, tableNumber, transactionType, notes, discountPercent, taxPercent, totals]);

  // Park order (save for later) - LEGACY
  const parkOrder = useCallback(async () => {
    if (items.length === 0) {
      setError('Cart is empty');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // New system doesn't have direct "park" endpoint
      // Instead, create an order and let it remain in 'pending' state
      // This functionality should be implemented via table transfer or order hold feature
      
      console.warn('parkOrder: Legacy function - parking functionality has been integrated into order management');
      throw new Error('Park order functionality requires table/order transfer feature. Please create order normally.');

    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to park order';
      setError(errorMessage);
      console.error('Error parking order:', err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [items]);

  return {
    // State
    items,
    notes,
    discountPercent,
    taxPercent,
    tableNumber,
    transactionType,
    loading,
    error,
    lastError,
    itemCount: items.length,
    totals,

    // Actions
    addItem,
    removeItem,
    updateQuantity,
    updateItemNotes,
    setNotes,
    setDiscountPercent,
    setTaxPercent,
    clearCart,
    
    // NEW: Restaurant-specific order creation
    createOrder,
    
    // LEGACY: Kept for backward compatibility
    submitCart,
    parkOrder,
    
    clearError: () => setError(null)
  };
};

export default useRestaurantCart;
